<script lang="ts">
  // BJL-CHECK: Card (used), Badge (used, bus route badges), Spinner (used), Button. The card-wide
  // open target is a bare custom <button> — a bjl Button carries its own chrome/sizing and can't
  // be a transparent full-bleed overlay sitting behind the ✕. See artifacts/deviations/FavoritesPage.md.
  //
  // Compact arrivals card for a single bus stop (SIRI StopMonitoring). One row per
  // route+direction, showing that line's soonest bus. Arrivals arrive already sorted
  // earliest-first from the backend, so the first hit per key is next.
  import { Spinner } from '@byjackli-labs/ui/Spinner'
  import { Colors } from '../../ui/colors'
  import { Type } from '../../ui/theme'
  import { useFavoritesStore } from '../../store/favorites'
  import { api, type BusArrival } from '../../services/api'
  import { usePollingResource } from '../../lib/polling.svelte'
  import { storeSelect } from '../../lib/store.svelte'
  import { formatCountdown, formatShortClock } from '../../ui/transitTime'
  import BusRouteBadge from '../../components/BusRouteBadge.svelte'
  import FavoriteArrivalCard from './FavoriteArrivalCard.svelte'

  interface Props {
    stopId: string
    stopName: string
    onRemove: () => void
    onOpen: () => void
  }

  const { stopId, stopName, onRemove, onOpen }: Props = $props()

  const favoriteBusRoutes = storeSelect(useFavoritesStore, (s) => s.favoriteBusRoutes)

  let arrivals = $state<BusArrival[] | null>(null)
  let loading = $state(true)

  usePollingResource(() => stopId, {
    intervalMs: 30_000,
    load: async (id) => (await api.getBusStopArrivals(id)).arrivals,
    onStart: () => { loading = true },
    onSuccess: (nextArrivals) => {
      arrivals = nextArrivals
      loading = false
    },
    onError: () => {
      arrivals = []
      loading = false
    },
  })

  // Collapse to one entry per route+direction, keeping the soonest bus. Favorited bus
  // lines then float to the top (stable otherwise, arrivals stay earliest-first),
  // mirroring how favorited subway lines sort at a station.
  const lines = $derived.by(() => {
    const out: Array<{ key: string; routeId: string; next: BusArrival }> = []
    if (arrivals) {
      const seen = new Set<string>()
      for (const a of arrivals) {
        const key = `${a.routeId}|${a.directionRef ?? ''}`
        if (seen.has(key)) continue
        seen.add(key)
        out.push({ key, routeId: a.routeId, next: a })
      }
    }
    return out.sort((a, b) => {
      const af = favoriteBusRoutes.value.has(a.routeId)
      const bf = favoriteBusRoutes.value.has(b.routeId)
      if (af === bf) return 0
      return af ? -1 : 1
    })
  })

  // A busy stop can serve six lines; past four the badges wrap onto a second row and push
  // the card taller than the number it exists to show. The rest become a "+2".
  const MAX_BADGES = 4

  // The one arrival the card reports: the soonest bus on the frontmost line, which — because
  // `lines` already floats favorited routes to the top — is the favorited line's next bus
  // whenever the rider has starred one. Split into value/unit so the number can be set at
  // 28px and its unit stay small beside it, matching the subway card.
  const next = $derived.by(() => {
    const first = lines[0]
    if (!first) return null
    const time = first.next.arrivalTime
    const detail = first.next.destinationName
      ? `${first.routeId} to ${first.next.destinationName}`
      : first.routeId
    if (time == null) return { value: '--', unit: '', isNow: false, detail }
    // A scheduled (unmonitored) bus has no live prediction behind it, so it is reported as a
    // clock time rather than a countdown — counting down to a guess overstates what we know.
    if (!first.next.monitored) {
      return {
        value: formatShortClock(time),
        unit: '',
        isNow: false,
        detail: `${detail} · scheduled`,
      }
    }
    const m = formatCountdown(time, { includeUnit: false })
    return { value: m, unit: m === 'Now' ? '' : 'min', isNow: m === 'Now', detail }
  })
</script>

{#snippet badges()}
  {#each lines.slice(0, MAX_BADGES) as l (l.key)}
    <div style="position: relative; flex-shrink: 0;">
      <BusRouteBadge routeId={l.routeId} size="xs" />
      {#if favoriteBusRoutes.value.has(l.routeId)}
        <span
          style="
            position: absolute; bottom: -3px; right: -3px;
            font-size: 10px; line-height: 1; pointer-events: none;
            color: {Colors.warning}; text-shadow: 0 0 3px rgba(0,0,0,0.9);
          "
        >★</span>
      {/if}
    </div>
  {/each}
  {#if lines.length > MAX_BADGES}
    <span style="align-self: center; color: {Colors.textMuted}; {Type.caption};">
      +{lines.length - MAX_BADGES}
    </span>
  {/if}
{/snippet}

{#snippet trailing()}
  {#if loading && !arrivals}
    <Spinner size="sm" label="Loading arrivals" />
  {:else if next}
    <div>
      <span style="color: {next.isNow ? Colors.success : Colors.text}; {Type.screen};">
        {next.value}
      </span>
      {#if next.unit}
        <span style="color: {Colors.textSecondary}; {Type.caption};"> {next.unit}</span>
      {/if}
    </div>
    <div
      style="
        color: {Colors.textMuted}; {Type.caption}; margin-top: 2px;
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 130px;
      "
    >{next.detail}</div>
  {:else}
    <div style="color: {Colors.textMuted}; {Type.caption};">No buses</div>
  {/if}
{/snippet}

<FavoriteArrivalCard
  title={stopName}
  {onRemove}
  {onOpen}
  {badges}
  {trailing}
  marginBottom={10}
/>
